import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Users,
  ShoppingBag,
  Wallet,
  Percent,
  Receipt,
  Clock3,
} from 'lucide-react';
import { useAppStore } from '../store';
import { useExpressCosts } from '../hooks/useExpressCosts';
import { formatCurrency } from '../utils/formatters';
import {
  AnalyticsPeriodPreset,
  buildInsights,
  computeAnalyticsSnapshot,
  formatPeriodLabel,
  formatPct,
  getComparablePreviousRange,
  getPeriodRange,
  pctChange,
} from '../utils/analytics';
import Input from '../components/ui/Input';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend
);

const PERIOD_OPTIONS: { value: AnalyticsPeriodPreset; label: string }[] = [
  { value: 'ytd', label: 'Año corriente' },
  { value: 'this_month', label: 'Este mes' },
  { value: 'last_month', label: 'Mes anterior' },
  { value: 'last_30', label: 'Últimos 30 días' },
  { value: 'last_90', label: 'Últimos 90 días' },
  { value: 'last_year', label: 'Año anterior' },
  { value: 'custom', label: 'Personalizado' },
];

const chartTooltip = {
  backgroundColor: 'rgba(17, 24, 39, 0.92)',
  titleFont: { size: 12, weight: '500' as const },
  bodyFont: { size: 12 },
  padding: 10,
  cornerRadius: 8,
  displayColors: true,
  callbacks: {
    label: (ctx: any) => {
      const v = ctx.parsed.y ?? ctx.parsed;
      if (typeof v !== 'number') return '';
      if (ctx.dataset.label?.includes('%')) return `${ctx.dataset.label}: ${v.toFixed(1)}%`;
      return `${ctx.dataset.label}: ${formatCurrency(v)}`;
    },
  },
};

const axisTicks = {
  color: '#9ca3af',
  font: { size: 11 },
};

function DeltaBadge({
  value,
  asPp = false,
}: {
  value: number | null;
  asPp?: boolean;
}) {
  if (value === null || Number.isNaN(value)) {
    return <span className="text-xs text-gray-400">sin base</span>;
  }
  const positive = value >= 0;
  const Icon = value === 0 ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  const label = asPp
    ? `${value > 0 ? '+' : ''}${value.toFixed(1).replace('.', ',')} pp`
    : formatPct(value);
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
        value === 0
          ? 'text-gray-500'
          : positive
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-rose-600 dark:text-rose-400'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

function KpiTile({
  label,
  value,
  delta,
  hint,
  accent,
  deltaAsPp = false,
}: {
  label: string;
  value: string;
  delta: number | null;
  hint?: string;
  accent?: string;
  deltaAsPp?: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900/60 p-4 hover:border-gray-300 dark:hover:border-gray-700 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </p>
        {accent ? <span className={`h-1.5 w-1.5 rounded-full ${accent}`} /> : null}
      </div>
      <p className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white tabular-nums">
        {value}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <DeltaBadge value={delta} asPp={deltaAsPp} />
        <span className="text-[11px] text-gray-400">{hint || 'vs. anterior'}</span>
      </div>
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  children,
  className = '',
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900/40 ${className}`}
    >
      <div className="px-5 pt-5 pb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
        ) : null}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}

function RankList({
  rows,
  valueKey,
  formatValue,
  showPct,
}: {
  rows: { name: string; value: number; pct?: number }[];
  valueKey?: string;
  formatValue: (n: number) => string;
  showPct?: boolean;
}) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <ul className="space-y-3">
      {rows.length === 0 ? (
        <li className="text-sm text-gray-400 py-6 text-center">Sin datos en el período</li>
      ) : (
        rows.map((row, idx) => (
          <li key={`${row.name}-${idx}`}>
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[11px] tabular-nums text-gray-400 w-4">{idx + 1}</span>
                <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{row.name}</span>
              </div>
              <div className="text-right shrink-0">
                <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-white">
                  {formatValue(row.value)}
                </span>
                {showPct && row.pct !== undefined ? (
                  <span className="ml-2 text-xs text-gray-400">{row.pct.toFixed(0)}%</span>
                ) : null}
              </div>
            </div>
            <div className="h-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-gray-900/80 dark:bg-gray-200/70"
                style={{ width: `${Math.max(4, (row.value / max) * 100)}%` }}
              />
            </div>
            {valueKey ? <span className="sr-only">{valueKey}</span> : null}
          </li>
        ))
      )}
    </ul>
  );
}

const Analytics: React.FC = () => {
  const {
    productions,
    samples,
    express,
    expressSamples,
    productionTasks,
    sampleTasks,
    expressItems,
    expressSampleItems,
    invoices,
    payments,
    clients,
    suppliers,
    darkMode,
  } = useAppStore();
  const { costs: expressCosts } = useExpressCosts();

  const [preset, setPreset] = useState<AnalyticsPeriodPreset>('ytd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const range = useMemo(
    () => getPeriodRange(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );
  const prevRange = useMemo(() => getComparablePreviousRange(range), [range]);

  const baseInput = useMemo(
    () => ({
      productions,
      samples,
      express,
      expressSamples,
      productionTasks,
      sampleTasks,
      expressItems,
      expressSampleItems,
      invoices,
      payments,
      clients,
      suppliers,
      expressCosts: {
        remera_cost: expressCosts.remera_cost || 0,
        buzo_cost: expressCosts.buzo_cost || 0,
      },
    }),
    [
      productions,
      samples,
      express,
      expressSamples,
      productionTasks,
      sampleTasks,
      expressItems,
      expressSampleItems,
      invoices,
      payments,
      clients,
      suppliers,
      expressCosts,
    ]
  );

  const current = useMemo(
    () => computeAnalyticsSnapshot({ ...baseInput, range }),
    [baseInput, range]
  );
  const previous = useMemo(
    () => computeAnalyticsSnapshot({ ...baseInput, range: prevRange }),
    [baseInput, prevRange]
  );

  const insights = useMemo(() => buildInsights(current, previous), [current, previous]);
  const periodLabel = formatPeriodLabel(range, preset);

  const gridColor = darkMode ? 'rgba(75, 85, 99, 0.35)' : 'rgba(229, 231, 235, 0.9)';
  const textMuted = darkMode ? '#9ca3af' : '#6b7280';

  const salesChart = useMemo(
    () => ({
      labels: current.salesSeries.map((p) => p.label),
      datasets: [
        {
          label: 'Ventas',
          data: current.salesSeries.map((p) => p.sales),
          borderColor: '#111827',
          backgroundColor: 'rgba(17, 24, 39, 0.06)',
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    }),
    [current.salesSeries]
  );

  const marginChart = useMemo(
    () => ({
      labels: current.salesSeries.map((p) => p.label),
      datasets: [
        {
          type: 'bar' as const,
          label: 'Margen $',
          data: current.salesSeries.map((p) => p.margin),
          backgroundColor: darkMode ? 'rgba(16, 185, 129, 0.45)' : 'rgba(16, 185, 129, 0.7)',
          borderRadius: 4,
          yAxisID: 'y',
        },
        {
          type: 'line' as const,
          label: 'Margen %',
          data: current.salesSeries.map((p) => p.marginPct),
          borderColor: '#0ea5e9',
          backgroundColor: '#0ea5e9',
          tension: 0.35,
          pointRadius: 2,
          borderWidth: 2,
          yAxisID: 'y1',
        },
      ],
    }),
    [current.salesSeries, darkMode]
  );

  const salesVsCollectedChart = useMemo(
    () => ({
      labels: current.salesSeries.map((p) => p.label),
      datasets: [
        {
          label: 'Ventas',
          data: current.salesSeries.map((p) => p.sales),
          backgroundColor: darkMode ? 'rgba(156, 163, 175, 0.5)' : 'rgba(17, 24, 39, 0.75)',
          borderRadius: 4,
        },
        {
          label: 'Cobranzas',
          data: current.salesSeries.map((p) => p.collected),
          backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.45)' : 'rgba(16, 185, 129, 0.75)',
          borderRadius: 4,
        },
      ],
    }),
    [current.salesSeries, darkMode]
  );

  const agingChart = useMemo(
    () => ({
      labels: current.aging.map((a) => a.label),
      datasets: [
        {
          label: 'Saldo',
          data: current.aging.map((a) => a.amount),
          backgroundColor: [
            'rgba(16, 185, 129, 0.75)',
            'rgba(250, 204, 21, 0.75)',
            'rgba(251, 146, 60, 0.8)',
            'rgba(249, 115, 22, 0.85)',
            'rgba(244, 63, 94, 0.85)',
          ],
          borderRadius: 6,
          barThickness: 28,
        },
      ],
    }),
    [current.aging]
  );

  const insightTone: Record<string, string> = {
    positive: 'bg-emerald-50 text-emerald-800 border-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/50',
    negative: 'bg-rose-50 text-rose-800 border-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/50',
    warning: 'bg-amber-50 text-amber-900 border-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900/50',
    neutral: 'bg-gray-50 text-gray-700 border-gray-100 dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-700',
  };

  return (
    <div className="space-y-8 max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-col gap-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
              Analítica
            </p>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-white capitalize">
              {periodLabel}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Panorama del negocio · ventas, margen, cobranzas y concentración de clientes
            </p>
          </div>
        </div>

        {/* Period filters */}
        <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-gray-100/80 dark:bg-gray-800/60 w-fit">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPreset(opt.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                preset === opt.value
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
            <Input
              label="Desde"
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <Input
              label="Hasta"
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {insights.map((insight) => (
            <div
              key={insight.id}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium ${insightTone[insight.tone]}`}
            >
              {insight.text}
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Ventas"
          value={formatCurrency(current.sales)}
          delta={pctChange(current.sales, previous.sales)}
          accent="bg-gray-900 dark:bg-white"
        />
        <KpiTile
          label="Margen bruto $"
          value={formatCurrency(current.grossMargin)}
          delta={pctChange(current.grossMargin, previous.grossMargin)}
          accent="bg-emerald-500"
        />
        <KpiTile
          label="Margen bruto %"
          value={`${current.grossMarginPct.toFixed(1).replace('.', ',')}%`}
          delta={current.grossMarginPct - previous.grossMarginPct}
          hint="vs. anterior"
          deltaAsPp
          accent="bg-sky-500"
        />
        <KpiTile
          label="Total cobrado"
          value={formatCurrency(current.collected)}
          delta={pctChange(current.collected, previous.collected)}
          accent="bg-emerald-600"
        />
        <KpiTile
          label="Pendiente de cobro"
          value={formatCurrency(current.receivable)}
          delta={null}
          hint="saldo actual"
          accent="bg-amber-500"
        />
        <KpiTile
          label="Ticket promedio"
          value={formatCurrency(current.ticketAvg)}
          delta={pctChange(current.ticketAvg, previous.ticketAvg)}
          hint="por factura"
        />
        <KpiTile
          label="Clientes activos"
          value={String(current.activeClients)}
          delta={pctChange(current.activeClients, previous.activeClients)}
        />
        <KpiTile
          label="Pedidos"
          value={String(current.orderCount)}
          delta={pctChange(current.orderCount, previous.orderCount)}
          hint="prod. + muestras + express"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Evolución de ventas" subtitle="Facturación a clientes por mes">
          <div className="h-64">
            {current.salesSeries.length > 0 ? (
              <Line
                data={salesChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false }, tooltip: chartTooltip },
                  scales: {
                    x: { grid: { display: false }, ticks: { ...axisTicks, color: textMuted } },
                    y: {
                      grid: { color: gridColor },
                      border: { display: false },
                      ticks: {
                        ...axisTicks,
                        color: textMuted,
                        callback: (v) => formatCurrency(Number(v)).replace(/\s/g, ''),
                      },
                    },
                  },
                }}
              />
            ) : (
              <EmptyChart />
            )}
          </div>
        </SectionCard>

        <SectionCard title="Margen bruto" subtitle="Valor prod./muestra − costos, por mes">
          <div className="h-64">
            {current.salesSeries.length > 0 ? (
              <Bar
                data={marginChart as any}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      align: 'end',
                      labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, color: textMuted, font: { size: 11 } },
                    },
                    tooltip: chartTooltip,
                  },
                  scales: {
                    x: { grid: { display: false }, ticks: { ...axisTicks, color: textMuted } },
                    y: {
                      position: 'left',
                      grid: { color: gridColor },
                      border: { display: false },
                      ticks: {
                        ...axisTicks,
                        color: textMuted,
                        callback: (v) => formatCurrency(Number(v)).replace(/\s/g, ''),
                      },
                    },
                    y1: {
                      position: 'right',
                      grid: { display: false },
                      border: { display: false },
                      ticks: {
                        ...axisTicks,
                        color: textMuted,
                        callback: (v) => `${v}%`,
                      },
                    },
                  },
                }}
              />
            ) : (
              <EmptyChart />
            )}
          </div>
        </SectionCard>

        <SectionCard title="Ventas vs cobranzas" subtitle="Facturación vs pagos recibidos">
          <div className="h-64">
            {current.salesSeries.length > 0 ? (
              <Bar
                data={salesVsCollectedChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      align: 'end',
                      labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, color: textMuted, font: { size: 11 } },
                    },
                    tooltip: chartTooltip,
                  },
                  scales: {
                    x: { grid: { display: false }, ticks: { ...axisTicks, color: textMuted } },
                    y: {
                      grid: { color: gridColor },
                      border: { display: false },
                      ticks: {
                        ...axisTicks,
                        color: textMuted,
                        callback: (v) => formatCurrency(Number(v)).replace(/\s/g, ''),
                      },
                    },
                  },
                }}
              />
            ) : (
              <EmptyChart />
            )}
          </div>
        </SectionCard>

        <SectionCard title="Aging de cuentas por cobrar" subtitle="Según fecha de vencimiento">
          <div className="h-64">
            <Bar
              data={agingChart}
              options={{
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: chartTooltip },
                scales: {
                  x: {
                    grid: { color: gridColor },
                    border: { display: false },
                    ticks: {
                      ...axisTicks,
                      color: textMuted,
                      callback: (v) => formatCurrency(Number(v)).replace(/\s/g, ''),
                    },
                  },
                  y: { grid: { display: false }, ticks: { ...axisTicks, color: textMuted } },
                },
              }}
            />
          </div>
        </SectionCard>
      </div>

      {/* Clients & concentration */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Top clientes por facturación" subtitle="Facturas del período (igual que Dashboard)">
          <RankList
            rows={current.topClientsBySales.map((c) => ({
              name: c.clientName,
              value: c.sales,
              pct: current.sales > 0 ? (c.sales / current.sales) * 100 : 0,
            }))}
            formatValue={formatCurrency}
            showPct
          />
        </SectionCard>

        <SectionCard title="Top clientes por margen" subtitle="Valor prod./muestra − costos del período">
          <RankList
            rows={current.topClientsByMargin.map((c) => ({
              name: c.clientName,
              value: c.margin,
            }))}
            formatValue={formatCurrency}
          />
        </SectionCard>

        <SectionCard title="Margen por cliente" subtitle="Solo producciones (sin muestras)">
          <RankList
            rows={(current.clientsByProductionMargin ?? []).map((c) => ({
              name: c.clientName,
              value: c.margin,
            }))}
            formatValue={formatCurrency}
          />
        </SectionCard>
      </div>

      {/* Concentration + client mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Concentración de ventas" subtitle="Sobre facturación a clientes">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Top 1', value: current.concentration.top1 },
              { label: 'Top 3', value: current.concentration.top3 },
              { label: 'Top 5', value: current.concentration.top5 },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-4 text-center"
              >
                <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">{item.label}</p>
                <p className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-white">
                  {item.value.toFixed(0)}%
                </p>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            {[
              { label: 'Top 1', value: current.concentration.top1, color: 'bg-gray-900 dark:bg-gray-200' },
              { label: 'Top 3', value: current.concentration.top3, color: 'bg-gray-600 dark:bg-gray-400' },
              { label: 'Top 5', value: current.concentration.top5, color: 'bg-gray-400 dark:bg-gray-500' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-10">{item.label}</span>
                <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${item.color}`}
                    style={{ width: `${Math.min(100, item.value)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Clientes nuevos, recurrentes e inactivos" subtitle="Mix de la base activa">
          <div className="grid grid-cols-3 gap-3 mb-5">
            <MixStat icon={<Users className="h-4 w-4" />} label="Nuevos" value={current.clientMix.new} />
            <MixStat icon={<ShoppingBag className="h-4 w-4" />} label="Recurrentes" value={current.clientMix.recurring} />
            <MixStat icon={<Clock3 className="h-4 w-4" />} label="Inactivos" value={current.clientMix.inactive} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            Nuevos: primera actividad en el período. Recurrentes: ya operaban antes. Inactivos: tuvieron
            actividad en el año previo y no en este período.
          </p>
        </SectionCard>
      </div>

      {/* Costs & low margin */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Costos por proveedor" subtitle="Tareas de producción y muestras">
          <RankList
            rows={current.costsBySupplier.map((s) => ({
              name: s.supplierName,
              value: s.cost,
            }))}
            formatValue={formatCurrency}
          />
        </SectionCard>

        <SectionCard title="Costos por cliente" subtitle="CMV atribuido al cliente">
          <RankList
            rows={current.costsByClient.map((c) => ({
              name: c.clientName,
              value: c.costs,
            }))}
            formatValue={formatCurrency}
          />
        </SectionCard>

        <SectionCard title="Margen por producción" subtitle="Mejores producciones del período">
          <RankList
            rows={current.marginByProduction.map((o) => ({
              name: `${o.name} · ${o.clientName}`,
              value: o.margin,
              pct: o.marginPct,
            }))}
            formatValue={formatCurrency}
            showPct
          />
        </SectionCard>

        <SectionCard title="Pedidos con menor margen" subtitle="Oportunidades de mejora">
          <ul className="space-y-3">
            {current.lowMarginOrders.length === 0 ? (
              <li className="text-sm text-gray-400 py-6 text-center">Sin datos</li>
            ) : (
              current.lowMarginOrders.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white truncate">{o.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {o.clientName} · {typeLabel(o.type)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p
                      className={`text-sm font-medium tabular-nums ${
                        o.marginPct < 20 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {o.marginPct.toFixed(1).replace('.', ',')}%
                    </p>
                    <p className="text-xs text-gray-400">{formatCurrency(o.margin)}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        </SectionCard>
      </div>

      {/* Period comparison strip */}
      <SectionCard title="Comparación entre períodos" subtitle={`Actual vs ${formatPeriodLabel(prevRange, 'custom')}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CompareCell
            icon={<Receipt className="h-4 w-4" />}
            label="Ventas"
            current={formatCurrency(current.sales)}
            previous={formatCurrency(previous.sales)}
            delta={pctChange(current.sales, previous.sales)}
          />
          <CompareCell
            icon={<Wallet className="h-4 w-4" />}
            label="Margen $"
            current={formatCurrency(current.grossMargin)}
            previous={formatCurrency(previous.grossMargin)}
            delta={pctChange(current.grossMargin, previous.grossMargin)}
          />
          <CompareCell
            icon={<Percent className="h-4 w-4" />}
            label="Margen %"
            current={`${current.grossMarginPct.toFixed(1).replace('.', ',')}%`}
            previous={`${previous.grossMarginPct.toFixed(1).replace('.', ',')}%`}
            delta={current.grossMarginPct - previous.grossMarginPct}
            deltaAsPp
          />
          <CompareCell
            icon={<Users className="h-4 w-4" />}
            label="Clientes activos"
            current={String(current.activeClients)}
            previous={String(previous.activeClients)}
            delta={pctChange(current.activeClients, previous.activeClients)}
          />
        </div>
      </SectionCard>

      <p className="text-[11px] text-gray-400 pb-4">
        Ventas = facturación a clientes del período (misma base que el Dashboard). Margen bruto =
        valor de producción/muestra (y express) cargados en el período − costos directos de tareas.
        Facturar y cargar una producción pueden caer en meses distintos.
      </p>
    </div>
  );
};

function EmptyChart() {
  return (
    <div className="h-full flex items-center justify-center text-sm text-gray-400">
      Sin datos en el período
    </div>
  );
}

function MixStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-4">
      <div className="flex items-center gap-2 text-gray-500 mb-2">
        {icon}
        <span className="text-[11px] uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

function CompareCell({
  icon,
  label,
  current,
  previous,
  delta,
  deltaAsPp = false,
}: {
  icon: React.ReactNode;
  label: string;
  current: string;
  previous: string;
  delta: number | null;
  deltaAsPp?: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-4">
      <div className="flex items-center gap-2 text-gray-500 mb-3">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{current}</p>
      <p className="text-xs text-gray-400 mt-1">Antes: {previous}</p>
      <div className="mt-2">
        <DeltaBadge value={delta} asPp={deltaAsPp} />
      </div>
    </div>
  );
}

function typeLabel(type: string) {
  switch (type) {
    case 'production':
      return 'Producción';
    case 'sample':
      return 'Muestra';
    case 'express':
      return 'Express';
    case 'express_sample':
      return 'Muestra Express';
    default:
      return type;
  }
}

export default Analytics;
